import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["closeable", "closeDisabledMessage", "draggable", "hideDefaultTooltip", "isCloseDisabled", "isDragging", "onClose", "dragHandle", "DragHandle", "truncate", "use", "onMouseEnter", "onMouseLeave", "onClick", "title", "children"];
import styled, { useTheme } from 'foundations-theming/utils';
import { CloseButton } from '../CloseButton/CloseButton';
import { DragHandle } from '../DragHandle/DragHandle';
import { Tooltip } from '../Tooltip/Tooltip';
import { TruncateString } from '../TruncateString/TruncateString';
import { setInputAppearance } from '../../utils/styles';
import { rgba } from '../../utils/color';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { MoveableObjectProvider } from './MoveableObjectProvider';
import { forwardRef } from 'react';
import { JUPITER_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Outer = styled.div.withConfig({
  displayName: "MoveableObject__Outer",
  componentId: "r7hlgq-0"
})(({
  $use,
  $isDragging,
  $cssStyles,
  theme
}) => {
  return Object.assign({
    position: 'relative'
  }, setInputAppearance(theme), {
    paddingBlockStart: `${parseInt(theme.space['250'], 10) - 1}px`,
    paddingInlineEnd: '30px',
    // No token for this
    paddingBlockEnd: `${parseInt(theme.space['250'], 10) - 1}px`,
    paddingInlineStart: '26px',
    // No token for this
    fontSize: theme.typography['body-100'].fontSize,
    backgroundColor: $use === 'on-dark' ? theme.color['fill-surface-default'] : theme.color['fill-tertiary-default']
  }, $isDragging ? {
    cursor: 'grabbing',
    boxShadow: `0 0 12px 0 ${rgba(theme.color['specialty-scrim'], 0.1)}`,
    // TODO why are we changing the color via rgba() instead of using it as is given it already has opacity?
    zIndex: JUPITER_LAYER
  } : {}, {
    '& + &': {
      marginBlockStart: theme.space['300']
    }
  }, $cssStyles);
});
const TooltipAnchor = styled.span.withConfig({
  displayName: "MoveableObject__TooltipAnchor",
  componentId: "r7hlgq-1"
})(({
  theme
}) => {
  return {
    blockSize: '100%',
    position: 'absolute',
    insetInlineEnd: theme.space['0'],
    insetBlockStart: theme.space['0'],
    inlineSize: '30px'
  };
});
export const MoveableObject = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      closeable = true,
      closeDisabledMessage,
      draggable = true,
      hideDefaultTooltip = false,
      isCloseDisabled = false,
      isDragging = false,
      onClose,
      dragHandle,
      DragHandle: DragHandleProp = DragHandle,
      truncate = true,
      use = 'default',
      onMouseEnter,
      onMouseLeave,
      onClick,
      title,
      children
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const {
    domProps,
    tokenProps
  } = filterProps(restProps);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const closeButtonStyleOverrides = {
    insetBlockStart: theme.space['400'],
    marginInlineEnd: `-${theme.space['300']}`
  };
  const content = truncate ? /*#__PURE__*/_jsx(TruncateString, {
    hideDefaultTooltip: hideDefaultTooltip,
    children: children
  }) : children;
  const renderedChildren = /*#__PURE__*/_jsx(MoveableObjectProvider, {
    closeButtonStyleOverrides: closeButtonStyleOverrides,
    children: /*#__PURE__*/_jsxs(Outer, Object.assign({}, domProps, {
      ref: ref,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onClick: onClick,
      title: title,
      $use: use,
      $isDragging: isDragging,
      $cssStyles: cssStyles,
      children: [content, closeable && /*#__PURE__*/_jsx(Tooltip, {
        title: closeDisabledMessage,
        children: /*#__PURE__*/_jsx(TooltipAnchor, {
          children: /*#__PURE__*/_jsx(CloseButton, {
            color: "icon-core-subtle",
            disabled: isCloseDisabled,
            onClick: onClose,
            size: "sm"
          })
        })
      })]
    }))
  });
  if (draggable) {
    const ComputedDragHandle = dragHandle || DragHandleProp;
    return /*#__PURE__*/_jsx(MoveableObjectProvider, {
      closeButtonStyleOverrides: closeButtonStyleOverrides,
      children: /*#__PURE__*/_jsx(ComputedDragHandle, {
        children: renderedChildren
      })
    });
  }
  return renderedChildren;
});